/**
 * HTTP Long Polling hook - same interface as useWebSocket
 * Uses HTTP requests instead of WebSocket for Discord Activities compatibility
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { WSMessage } from '../types/game';
import { getApiBaseUrl, discordFetch } from '../discord/discordSdk';

interface UseLongPollingOptions {
  roomId: string;
  oderId: string;       // Client ID (Discord user ID)
  enabled?: boolean;
  onMessage?: (message: WSMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
}

interface UseLongPollingReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  sendMessage: (message: WSMessage) => void;
  disconnect: () => void;
  reconnect: () => void;
}

export function useLongPolling({
  roomId,
  oderId,
  enabled = true,
  onMessage,
  onOpen,
  onClose,
  onError,
}: UseLongPollingOptions): UseLongPollingReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const connectionIdRef = useRef(0);
  const pollAbortRef = useRef<AbortController | null>(null);
  const lastSeqRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 2000;
  const POLL_TIMEOUT = 30000; // 30 seconds

  const getBaseUrl = useCallback(() => {
    return `${getApiBaseUrl()}/room/${roomId}`;
  }, [roomId]);

  /**
   * Connect to the server
   */
  const connect = useCallback(async () => {
    if (!isMountedRef.current) return;

    connectionIdRef.current += 1;
    const currentConnectionId = connectionIdRef.current;

    setIsConnecting(true);
    setError(null);

    try {
      const url = `${getBaseUrl()}/connect?clientId=${encodeURIComponent(oderId)}`;
      console.log(`Long polling: Connecting to ${url}`);

      const response = await discordFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!isMountedRef.current || currentConnectionId !== connectionIdRef.current) {
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Connection failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('Long polling: Connected', data);

      // Process any initial messages
      if (data.messages && Array.isArray(data.messages)) {
        for (const msg of data.messages) {
          if (msg.seq) lastSeqRef.current = Math.max(lastSeqRef.current, msg.seq);
          onMessage?.(msg.message || msg);
        }
      }

      setIsConnected(true);
      setIsConnecting(false);
      reconnectAttemptsRef.current = 0;
      onOpen?.();

      // Start polling loop
      startPolling(currentConnectionId);
    } catch (err) {
      if (!isMountedRef.current || currentConnectionId !== connectionIdRef.current) {
        return;
      }

      console.error('Long polling: Connection failed', err);
      setError(err instanceof Error ? err.message : 'Connection failed');
      setIsConnecting(false);
      onError?.(err instanceof Error ? err : new Error('Connection failed'));

      // Auto-reconnect
      scheduleReconnect();
    }
  }, [getBaseUrl, oderId, onMessage, onOpen, onError]);

  /**
   * Start the polling loop
   */
  const startPolling = useCallback(async (connectionId: number) => {
    while (isMountedRef.current && connectionId === connectionIdRef.current) {
      try {
        pollAbortRef.current = new AbortController();
        const timeoutId = setTimeout(() => {
          pollAbortRef.current?.abort();
        }, POLL_TIMEOUT + 5000); // Extra buffer for network

        const url = `${getBaseUrl()}/poll?clientId=${encodeURIComponent(oderId)}&since=${lastSeqRef.current}`;

        const response = await discordFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: pollAbortRef.current.signal,
        });

        clearTimeout(timeoutId);

        if (!isMountedRef.current || connectionId !== connectionIdRef.current) {
          return;
        }

        if (!response.ok) {
          if (response.status === 404 || response.status === 410) {
            // Client not found - need to reconnect
            throw new Error('Session expired');
          }
          throw new Error(`Poll failed: ${response.status}`);
        }

        const data = await response.json();

        // Process messages
        if (data.messages && Array.isArray(data.messages)) {
          for (const msg of data.messages) {
            if (msg.seq) lastSeqRef.current = Math.max(lastSeqRef.current, msg.seq);
            onMessage?.(msg.message || msg);
          }
        }
      } catch (err) {
        if (!isMountedRef.current || connectionId !== connectionIdRef.current) {
          return;
        }

        // Ignore abort errors (normal timeout)
        if (err instanceof Error && err.name === 'AbortError') {
          continue;
        }

        console.error('Long polling: Poll error', err);
        setIsConnected(false);
        setError(err instanceof Error ? err.message : 'Poll failed');
        onClose?.();

        // Schedule reconnect
        scheduleReconnect();
        return;
      }
    }
  }, [getBaseUrl, oderId, onMessage, onClose]);

  /**
   * Schedule a reconnection attempt
   */
  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return;
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setError('Connection lost. Please reconnect manually.');
      return;
    }

    reconnectAttemptsRef.current += 1;
    console.log(`Long polling: Reconnecting in ${RECONNECT_DELAY}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);

    reconnectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, RECONNECT_DELAY);
  }, [connect]);

  /**
   * Send a message to the server
   */
  const sendMessage = useCallback(async (message: WSMessage) => {
    if (!isConnected) {
      console.error('Long polling: Not connected');
      setError('Not connected to server');
      return;
    }

    try {
      const url = `${getBaseUrl()}/send?clientId=${encodeURIComponent(oderId)}`;

      const response = await discordFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(message),
      });

      if (!response.ok) {
        throw new Error(`Send failed: ${response.status}`);
      }
    } catch (err) {
      console.error('Long polling: Send failed', err);
      setError(err instanceof Error ? err.message : 'Send failed');
      onError?.(err instanceof Error ? err : new Error('Send failed'));
    }
  }, [isConnected, getBaseUrl, oderId, onError]);

  /**
   * Disconnect from the server
   */
  const disconnect = useCallback(async () => {
    // Cancel any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Abort any pending poll
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;

    // Increment connection ID to invalidate current session
    connectionIdRef.current += 1;
    reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS; // Prevent auto-reconnect

    // Notify server
    if (isConnected) {
      try {
        const url = `${getBaseUrl()}/disconnect?clientId=${encodeURIComponent(oderId)}`;
        await discordFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (err) {
        // Ignore disconnect errors
      }
    }

    if (isMountedRef.current) {
      setIsConnected(false);
      setIsConnecting(false);
    }
  }, [isConnected, getBaseUrl, oderId]);

  /**
   * Manually reconnect
   */
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connectionIdRef.current += 1;
    setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, 100);
  }, [connect, disconnect]);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-connect when enabled
  useEffect(() => {
    if (enabled && roomId && oderId && isMountedRef.current) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [roomId, oderId, enabled]);

  return {
    isConnected,
    isConnecting,
    error,
    sendMessage,
    disconnect,
    reconnect,
  };
}
